/**
 * Currency formatting utilities — symbol lookup, decimal handling,
 * and Intl.NumberFormat-based money formatting from minor units (cents).
 */

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  decimalPlaces: number;
}

const SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF',
  CNY: '¥', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  RON: 'lei', BGN: 'лв', HRK: 'kn', RUB: '₽', UAH: '₴', TRY: '₺', BRL: 'R$',
  MXN: '$', ZAR: 'R', INR: '₹', KRW: '₩', THB: '฿', VND: '₫', IDR: 'Rp',
  MYR: 'RM', SGD: 'S$', PHP: '₱', TWD: 'NT$', HKD: 'HK$', ILS: '₪', AED: 'د.إ',
  SAR: 'ر.س', QAR: 'ر.ق', KWD: 'د.ك', BHD: 'د.ب', OMR: 'ر.ع.', EGP: '£',
  NGN: '₦', KES: 'KSh', GHS: '₵', COP: '$', ARS: '$', CLP: '$', PEN: 'S/',
  CRC: '₡', DOP: 'RD$', JMD: 'J$', BOB: 'Bs', PYG: '₲', GTQ: 'Q',
  NIO: 'C$', PAB: 'B/.', PKR: '₨', BDT: '৳', LKR: '₨', NPR: '₨',
  GEL: '₾', AMD: '֏', AZN: '₼', KZT: '₸',
};

const NAMES: Record<string, string> = {
  USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', JPY: 'Japanese Yen',
  CAD: 'Canadian Dollar', AUD: 'Australian Dollar', CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan', SEK: 'Swedish Krona', NOK: 'Norwegian Krone',
  DKK: 'Danish Krone', PLN: 'Polish Zloty', CZK: 'Czech Koruna', HUF: 'Hungarian Forint',
  BRL: 'Brazilian Real', MXN: 'Mexican Peso', INR: 'Indian Rupee', KRW: 'South Korean Won',
  THB: 'Thai Baht', SGD: 'Singapore Dollar', HKD: 'Hong Kong Dollar', TWD: 'Taiwan Dollar',
  ILS: 'Israeli Shekel', AED: 'UAE Dirham', SAR: 'Saudi Riyal', ZAR: 'South African Rand',
  TRY: 'Turkish Lira', RUB: 'Russian Ruble', NGN: 'Nigerian Naira',
};

const ZERO_DECIMAL: Set<string> = new Set([
  'JPY', 'KRW', 'VND', 'IDR', 'LAK', 'KHR', 'MMK', 'PYG',
]);

export class CurrencyUtils {
  static getSymbol(code: string): string {
    return SYMBOLS[code] ?? code;
  }

  static getName(code: string): string {
    return NAMES[code] ?? code;
  }

  static getDecimalPlaces(code: string): number {
    return ZERO_DECIMAL.has(code) ? 0 : 2;
  }

  static getCurrency(code: string): Currency {
    return {
      code,
      symbol: CurrencyUtils.getSymbol(code),
      name: CurrencyUtils.getName(code),
      decimalPlaces: CurrencyUtils.getDecimalPlaces(code),
    };
  }
}

/**
 * Format an amount expressed in minor units (cents) into a locale-aware string.
 *
 * @example
 * formatMoney(12345, 'USD')           // "$123.45"
 * formatMoney(10000, 'EUR', 'de-DE')  // "100,00 €"
 * formatMoney(1000, 'JPY')            // "¥1,000"
 */
export function formatMoney(amountMinorUnits: number, currencyCode = 'USD', locale = 'en-US'): string {
  const decimals = CurrencyUtils.getDecimalPlaces(currencyCode);
  const value = amountMinorUnits / (decimals === 0 ? 100 : Math.pow(10, decimals));

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch {
    return `${CurrencyUtils.getSymbol(currencyCode)}${value.toFixed(decimals)}`;
  }
}
